import {
  clear_token,
  get_token,
  get_user_profile,
  is_authenticated
} from "@/router";

const default_inactive_timeout_ms = 10 * 60 * 1000;
const default_prompt_timeout_ms = 2 * 60 * 1000;
const countdown_interval_ms = 1000;
const activity_events = [
  "click",
  "keydown",
  "mousemove",
  "mousedown",
  "scroll",
  "touchstart"
];

export default {
  name: "SessionTimeoutPrompt",

  data() {
    return ({
      inactivity_timer_id: null,
      logout_timer_id: null,
      countdown_timer_id: null,
      show_session_prompt: false,
      prompt_expires_at: null,
      inactive_timeout_ms: default_inactive_timeout_ms,
      prompt_timeout_ms: default_prompt_timeout_ms,
      remaining_prompt_ms: default_prompt_timeout_ms
    });
  },

  computed: {
    countdown_minutes() {
      const total_seconds = Math.max(0, Math.ceil(this.remaining_prompt_ms / 1000));
      const minutes = Math.floor(total_seconds / 60);
      const seconds = String(total_seconds % 60).padStart(2, "0");
      return `${minutes}:${seconds}`;
    }
  },

  async mounted() {
    activity_events.forEach((event_name) => {
      window.addEventListener(event_name, this.handle_user_activity, { passive: true });
    });

    await this.load_session_timeout_config();
    this.reset_inactivity_timer();
  },

  beforeUnmount() {
    activity_events.forEach((event_name) => {
      window.removeEventListener(event_name, this.handle_user_activity);
    });
    this.clear_all_timers();
  },

  watch: {
    "$route"() {
      this.reset_inactivity_timer();
    }
  },

  methods: {
    async load_session_timeout_config() {
      const config_url = this.get_session_timeout_config_url();

      if (!config_url) {
        return;
      }

      try {
        const response = await fetch(config_url, {
          method: "GET",
          headers: {
            Accept: "application/json"
          }
        });

        if (!response.ok) {
          throw new Error(`Session timeout config failed with status ${response.status}`);
        }

        const config = await response.json();
        this.apply_session_timeout_config(config);
      } catch (error) {
        console.error("Could not load session timeout config:", error);
      }
    },

    get_session_timeout_config_url() {
      const logout_url = process.env.VUE_APP_LOGOUT_URL;

      if (!logout_url) {
        return "";
      }

      return logout_url.replace(/logout_api\/?$/, "session_timeout_config/");
    },

    apply_session_timeout_config(config) {
      const inactive_timeout_ms = Number(config?.inactivity_timeout_ms);
      const prompt_timeout_ms = Number(config?.prompt_timeout_ms);

      if (Number.isFinite(inactive_timeout_ms) && inactive_timeout_ms > 0) {
        this.inactive_timeout_ms = inactive_timeout_ms;
      }

      if (Number.isFinite(prompt_timeout_ms) && prompt_timeout_ms > 0) {
        this.prompt_timeout_ms = prompt_timeout_ms;
        this.remaining_prompt_ms = prompt_timeout_ms;
      }
    },

    handle_user_activity() {
      if (this.show_session_prompt) {
        return;
      }

      this.reset_inactivity_timer();
    },

    reset_inactivity_timer() {
      clearTimeout(this.inactivity_timer_id);
      this.inactivity_timer_id = null;

      if (!is_authenticated()) {
        this.clear_prompt_timers();
        this.show_session_prompt = false;
        return;
      }

      this.inactivity_timer_id = setTimeout(() => {
        this.show_inactivity_prompt();
      }, this.inactive_timeout_ms);
    },

    show_inactivity_prompt() {
      if (!is_authenticated()) {
        return;
      }

      this.show_session_prompt = true;
      this.prompt_expires_at = Date.now() + this.prompt_timeout_ms;
      this.remaining_prompt_ms = this.prompt_timeout_ms;

      this.logout_timer_id = setTimeout(() => {
        this.logout_due_to_inactivity();
      }, this.prompt_timeout_ms);

      this.countdown_timer_id = setInterval(() => {
        this.remaining_prompt_ms = this.prompt_expires_at - Date.now();
      }, countdown_interval_ms);
    },

    continue_session() {
      this.clear_prompt_timers();
      this.show_session_prompt = false;
      this.reset_inactivity_timer();
    },

    async logout_due_to_inactivity() {
      this.clear_all_timers();
      this.show_session_prompt = false;

      const logout_url = process.env.VUE_APP_LOGOUT_URL;
      const auth_token = get_token();
      const profile = get_user_profile();
      const user_id = profile?.id || profile?.user_id || sessionStorage.getItem("user_id");

      if (logout_url && auth_token) {
        try {
          await fetch(logout_url, {
            method: "POST",
            headers: {
              Authorization: `Token ${auth_token}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ user_id })
          });
        } catch (error) {
          console.error("Automatic logout failed:", error);
        }
      }

      clear_token();
      localStorage.clear();
      sessionStorage.clear();

      if (this.$route.path !== "/login") {
        this.$router.replace("/login");
      }
    },

    clear_prompt_timers() {
      clearTimeout(this.logout_timer_id);
      clearInterval(this.countdown_timer_id);
      this.logout_timer_id = null;
      this.countdown_timer_id = null;
      this.prompt_expires_at = null;
      this.remaining_prompt_ms = this.prompt_timeout_ms;
    },

    clear_all_timers() {
      clearTimeout(this.inactivity_timer_id);
      this.inactivity_timer_id = null;
      this.clear_prompt_timers();
    }
  }
};
